import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import {
    Users, Calendar, MessageCircle, MapPin, PartyPopper, Search,
    LayoutGrid, Settings, UserCircle, LogOut, BarChart3, ChevronLeft, ChevronRight,
    Briefcase, Folder, X, Building2, HeadphonesIcon, Minus, Globe, ChevronDown, FolderPlus, Trash2, Coins,
    Package, Image
} from 'lucide-react';
import { useTranslation, useLanguage } from '../../context/LanguageContext';
import { useTheme } from '../../context/ThemeContext';
import { useAuth, getStoredUser } from '../../context/AuthContext';
import logo from '../../assets/logo.png';
import { getPeoplePreferences, savePeoplePreferences } from '../../services/apiService';
import { getModules, getPeopleModules, MODULE_NAV_MAP, removeModuleFromGroup, addModuleToGroup, createModuleGroup, deleteModuleGroup } from '../../services/modulesService';
import { getUnreadCount } from '../../services/messagesService';
import MouseGlow from '../ui/MouseGlow';

// Module icons mapping
const MODULE_ICONS = {
    'CONTACTS': Users,
    'CONVERSATIONS': MessageCircle,
    'MAP': MapPin,
    'AGENDA': Calendar,
    'EVENTS': PartyPopper,
    'PLACES': Building2,
    'SUPPORT': HeadphonesIcon,
    'WORK': Briefcase,
    'OFFERS': Coins,
    'PRODUCTS': Package,
    'ADVERTISEMENTS': Image,
    'WEBSITES': Globe
};

// Module colors
const MODULE_COLORS = {
    'CONTACTS': '#3b82f6',
    'CONVERSATIONS': '#8b5cf6',
    'MAP': '#22c55e',
    'AGENDA': '#f97316',
    'EVENTS': '#ec4899',
    'PLACES': '#14b8a6',
    'SUPPORT': '#6366f1',
    'WORK': '#0ea5e9',
    'OFFERS': '#10b981',
    'PRODUCTS': '#f59e0b',
    'ADVERTISEMENTS': '#ef4444',
    'WEBSITES': '#06b6d4'
};

// Nav ID to code mapping
const NAV_TO_CODE = {
    'contacts': 'CONTACTS',
    'messages': 'CONVERSATIONS',
    'map': 'MAP',
    'calendar': 'AGENDA',
    'events': 'EVENTS',
    'places': 'PLACES',
    'support': 'SUPPORT',
    'work': 'WORK',
    'offers': 'OFFERS',
    'products': 'PRODUCTS',
    'advertisements': 'ADVERTISEMENTS',
    'websites': 'WEBSITES'
};

// Fixed dock apps (always present, not movable)
const DOCK_APPS = [
    { id: 'dashboard', icon: BarChart3, color: '#8b5cf6', navId: 'dashboard' },
    { id: 'modules', icon: LayoutGrid, color: '#6366f1', navId: 'modules' },
    { id: 'search', icon: Search, color: '#f59e0b', navId: 'search' },
    { id: 'profile', icon: UserCircle, color: '#3b82f6', navId: 'profile' },
    { id: 'preferences', icon: Settings, color: '#64748b', navId: 'preferences' }
];

// Default gradient fallback
const DEFAULT_GRADIENT = 'linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)';

// Helper to normalize keys to lowercase
const normalizeKeys = (obj) => {
    if (!obj || typeof obj !== 'object') return obj;
    if (Array.isArray(obj)) return obj.map(normalizeKeys);
    return Object.keys(obj).reduce((acc, key) => {
        const lowerKey = key.toLowerCase();
        acc[lowerKey] = normalizeKeys(obj[key]);
        return acc;
    }, {});
};

/**
 * Dashboard Language Switcher - Style adapté au dashboard
 */
const DashboardLanguageSwitcher = () => {
    const { language, setLanguage, languages } = useLanguage();
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    const currentLanguage = languages.find(lang => lang.code === language) || languages[0];

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleLanguageChange = (langCode) => {
        setLanguage(langCode);
        setIsOpen(false);
    };

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium bg-white/20 hover:bg-white/30 text-white rounded-full transition-colors"
            >
                <span className="uppercase">{currentLanguage.code}</span>
                <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {isOpen && (
                <div className="absolute left-0 top-full mt-2 bg-white rounded-xl shadow-xl border border-gray-100 py-1 min-w-[150px] z-[9999]">
                    {languages.map((lang) => (
                        <button
                            key={lang.code}
                            onClick={() => handleLanguageChange(lang.code)}
                            className={`w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 flex items-center gap-2 transition-colors ${
                                lang.code === language ? 'bg-purple-50 text-purple-600 font-medium' : 'text-gray-700'
                            }`}
                        >
                            <span className="w-5 text-center font-medium text-xs uppercase opacity-60">{lang.flag}</span>
                            <span>{lang.name}</span>
                            {lang.code === language && (
                                <span className="ml-auto w-2 h-2 rounded-full bg-purple-500" />
                            )}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

/**
 * Group Popup Modal - Shows modules inside a group
 */
const GroupPopupModal = ({ group, onClose, onNavigate, unreadMessages, t, language, onRemoveFromGroup, onDeleteGroup }) => {
    const popupRef = useRef(null);
    const [draggingModule, setDraggingModule] = useState(null);
    const dragDataRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            // Don't close if we're dragging
            if (draggingModule) return;
            if (popupRef.current && !popupRef.current.contains(e.target)) {
                onClose();
            }
        };
        const handleEscape = (e) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscape);
        };
    }, [onClose, draggingModule]);

    if (!group) return null;

    // Filter modules that are pinned/visible
    const visibleModules = (group.modules || []).filter(m => {
        const isPinned = m.ispinned === undefined || m.ispinned === null ||
                        m.ispinned === true || m.ispinned === 1 || m.ispinned === '1';
        return isPinned;
    });

    const getModuleIcon = (code) => MODULE_ICONS[code] || LayoutGrid;
    const getNavId = (code) => MODULE_NAV_MAP[code] || code.toLowerCase();

    // Handle drag start for module
    const handleModuleDragStart = (e, module) => {
        const navId = getNavId(module.code);
        const dragData = {
            type: 'module-from-group',
            moduleId: module.moduleid || module.id,
            moduleCode: module.code,
            navId: navId,
            groupId: group.id
        };
        dragDataRef.current = dragData;
        e.dataTransfer.setData('text/plain', JSON.stringify(dragData));
        e.dataTransfer.effectAllowed = 'move';
        setDraggingModule(module.moduleid || module.id);

        // Clone the icon element for drag image
        const iconElement = e.currentTarget.querySelector('[data-drag-icon]');
        if (iconElement) {
            const clone = iconElement.cloneNode(true);
            clone.style.position = 'fixed';
            clone.style.top = '-1000px';
            clone.style.left = '-1000px';
            clone.style.zIndex = '99999';
            clone.style.pointerEvents = 'none';
            document.body.appendChild(clone);
            e.dataTransfer.setDragImage(clone, 28, 28);

            // Remove clone after drag starts
            requestAnimationFrame(() => {
                setTimeout(() => {
                    if (clone.parentNode) {
                        document.body.removeChild(clone);
                    }
                }, 0);
            });
        }
    };

    // Handle drag end - check if dropped outside the popup
    const handleModuleDragEnd = (e) => {
        const moduleId = draggingModule;
        setDraggingModule(null);

        // Check if the drop happened outside the popup
        if (popupRef.current && dragDataRef.current) {
            const rect = popupRef.current.getBoundingClientRect();
            const x = e.clientX;
            const y = e.clientY;

            // If dropped outside the popup, remove from group
            if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
                if (onRemoveFromGroup) {
                    onRemoveFromGroup(dragDataRef.current);
                }
            }
        }
        dragDataRef.current = null;
    };

    return createPortal(
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[9999] p-4">
            <div
                ref={popupRef}
                className="bg-white rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden"
            >
                {/* Header */}
                <div
                    className="flex items-center justify-between p-4 text-white"
                    style={{ backgroundColor: group.color || '#8b5cf6' }}
                >
                    <div className="flex items-center gap-3">
                        <Folder className="w-6 h-6" />
                        <span className="font-semibold text-lg">{group.name}</span>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-white/20 rounded-full transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Modules Grid */}
                <div className="p-4">
                    {visibleModules.length === 0 ? (
                        <div className="text-center py-8">
                            <p className="text-gray-400 mb-4">
                                {t.modules?.noModules || 'No modules in this group'}
                            </p>
                            {onDeleteGroup && (
                                <button
                                    onClick={() => onDeleteGroup(group.id)}
                                    className="inline-flex items-center gap-2 px-4 py-2 text-red-500 hover:bg-red-50 rounded-xl transition-colors"
                                >
                                    <Trash2 className="w-4 h-4" />
                                    <span className="font-medium">{t.dashboard?.deleteFolder || 'Supprimer le dossier'}</span>
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className="grid grid-cols-3 gap-4">
                            {visibleModules.map((module) => {
                                const Icon = getModuleIcon(module.code);
                                const navId = getNavId(module.code);
                                const color = MODULE_COLORS[module.code] || '#8b5cf6';
                                const hasBadge = module.code === 'CONVERSATIONS' && unreadMessages > 0;
                                const isDragging = draggingModule === (module.moduleid || module.id);

                                return (
                                    <div
                                        key={module.itemid || module.moduleid}
                                        draggable
                                        onDragStart={(e) => handleModuleDragStart(e, module)}
                                        onDragEnd={handleModuleDragEnd}
                                        onClick={() => {
                                            if (!draggingModule) {
                                                onNavigate(navId);
                                                onClose();
                                            }
                                        }}
                                        className={`flex flex-col items-center gap-2 p-3 rounded-2xl hover:bg-gray-100 transition-all cursor-pointer select-none
                                            ${isDragging ? 'opacity-40 scale-95' : ''}`}
                                    >
                                        <div
                                            data-drag-icon
                                            className="relative w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg"
                                            style={{ backgroundColor: color }}
                                        >
                                            <Icon className="w-7 h-7 text-white" />
                                            {hasBadge && (
                                                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
                                                    {unreadMessages > 9 ? '9+' : unreadMessages}
                                                </span>
                                            )}
                                        </div>
                                        <span className="text-xs text-gray-700 font-medium text-center leading-tight max-w-[70px] truncate">
                                            {module.name || module.label || module[`name_${language}`]}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Hint for drag */}
                <div className="px-4 pb-4">
                    <p className="text-xs text-gray-400 text-center">
                        {t.modules?.dragToRemove || 'Drag a module outside to remove from folder'}
                    </p>
                </div>
            </div>
        </div>,
        document.body
    );
};

/**
 * Dashboard - Smartphone-style home screen with app icons
 */
const Dashboard = ({ onNavigate }) => {
    const navigate = useNavigate();
    const { logout } = useAuth();
    const { t, language } = useTranslation();
    const { currentTheme } = useTheme();
    const user = getStoredUser();
    const userId = parseInt(localStorage.getItem('userId'), 10);

    // Pinned items from sidebar (modules and groups)
    const [pinnedItems, setPinnedItems] = useState(() => {
        const stored = localStorage.getItem('cabazaPinnedItems');
        return stored ? JSON.parse(stored) : [];
    });

    // Groups data from sidebar
    const [groupsData, setGroupsData] = useState(() => {
        const stored = localStorage.getItem('cabazaGroupsData');
        return stored ? JSON.parse(stored) : [];
    });

    // All modules from API
    const [allModules, setAllModules] = useState([]);

    // Unread messages count
    const [unreadMessages, setUnreadMessages] = useState(0);

    // Open group popup
    const [openGroupPopup, setOpenGroupPopup] = useState(null);

    // Multi-desktop support: desktops is an array of arrays of pinned item identifiers
    // Format: [["module:contacts", "group:123", "module:messages"], [...]]
    const [desktops, setDesktops] = useState(() => {
        const stored = localStorage.getItem('cabazaDashboardDesktops');
        if (stored) {
            try {
                const parsed = JSON.parse(stored);
                if (Array.isArray(parsed) && parsed.length > 0) return parsed;
            } catch (e) { }
        }
        return [[]]; // Start with empty, will be populated from pinnedItems
    });

    // Current desktop index
    const [currentDesktop, setCurrentDesktop] = useState(0);


    // Drag state
    const dragItem = useRef(null);
    const dragOverItem = useRef(null);
    const [draggedId, setDraggedId] = useState(null);

    // Touch drag state
    const [touchDragInfo, setTouchDragInfo] = useState(null);
    const touchStartPos = useRef(null);
    const longPressTimer = useRef(null);
    const appRefs = useRef({});

    // Save timeout ref for debouncing
    const saveTimeoutRef = useRef(null);

    // Edge drag zone state
    const [edgeDragZone, setEdgeDragZone] = useState(null);
    const edgeDragTimer = useRef(null);

    // Drop target state
    const [dropTarget, setDropTarget] = useState(null);

    // Group drop target state (when dragging a module over a group)
    const [groupDropTarget, setGroupDropTarget] = useState(null);

    // Edge hover state (for showing arrows on mouse hover near edges)
    const [edgeHover, setEdgeHover] = useState(null); // 'left' | 'right' | null

    // Create folder dialog state
    const [showCreateFolderDialog, setShowCreateFolderDialog] = useState(false);
    const [newFolderName, setNewFolderName] = useState('');

    // Get current desktop apps (filtered by pinned items)
    const gridApps = desktops[currentDesktop] || [];

    // Track if data has been loaded
    const dataLoadedRef = useRef(false);

    // Load data on mount (only once)
    useEffect(() => {
        if (dataLoadedRef.current || !userId) return;
        dataLoadedRef.current = true;

        const loadData = async () => {
            try {
                const [modulesData, groupsDataFromApi, prefs, count] = await Promise.all([
                    getModules(language),
                    getPeopleModules(userId, language),
                    getPeoplePreferences(userId),
                    getUnreadCount()
                ]);

                const normalizedModules = normalizeKeys(modulesData);
                const normalizedGroups = normalizeKeys(groupsDataFromApi);

                setAllModules(normalizedModules);
                setGroupsData(normalizedGroups);
                setUnreadMessages(count);
                localStorage.setItem('cabazaGroupsData', JSON.stringify(normalizedGroups));

                // Load pinned items from API
                const pinnedItemsFromApi = prefs.pinnedItems || prefs.pinneditems || [];
                if (pinnedItemsFromApi.length > 0) {
                    setPinnedItems(pinnedItemsFromApi);
                    localStorage.setItem('cabazaPinnedItems', JSON.stringify(pinnedItemsFromApi));
                }

                // Load dashboard layout from API
                const dashboardDesktops = prefs.dashboardDesktops || prefs.dashboarddesktops;

                if (dashboardDesktops && Array.isArray(dashboardDesktops) && dashboardDesktops.length > 0) {
                    setDesktops(dashboardDesktops);
                    localStorage.setItem('cabazaDashboardDesktops', JSON.stringify(dashboardDesktops));
                }
            } catch (error) {
                console.error('Error loading dashboard data:', error);
            }
        };
        loadData();
    }, [userId]);

    // Listen for changes from sidebar/modules page
    useEffect(() => {
        const handlePinnedItemsChange = () => {
            const stored = localStorage.getItem('cabazaPinnedItems');
            setPinnedItems(stored ? JSON.parse(stored) : []);
        };

        const handleGroupsChange = async () => {
            const stored = localStorage.getItem('cabazaGroupsData');
            if (stored) {
                setGroupsData(JSON.parse(stored));
            } else if (userId) {
                const groupsDataFromApi = await getPeopleModules(userId, language);
                const normalizedGroups = normalizeKeys(groupsDataFromApi);
                setGroupsData(normalizedGroups);
                localStorage.setItem('cabazaGroupsData', JSON.stringify(normalizedGroups));
            }
        };

        window.addEventListener('pinnedItemsChanged', handlePinnedItemsChange);
        window.addEventListener('pinnedModulesChanged', handlePinnedItemsChange);
        window.addEventListener('pinnedGroupsChanged', handlePinnedItemsChange);
        window.addEventListener('modulesGroupsChanged', handleGroupsChange);
        window.addEventListener('modulePinnedToggled', handleGroupsChange);

        return () => {
            window.removeEventListener('pinnedItemsChanged', handlePinnedItemsChange);
            window.removeEventListener('pinnedModulesChanged', handlePinnedItemsChange);
            window.removeEventListener('pinnedGroupsChanged', handlePinnedItemsChange);
            window.removeEventListener('modulesGroupsChanged', handleGroupsChange);
            window.removeEventListener('modulePinnedToggled', handleGroupsChange);
        };
    }, [userId, language]);

    // Max apps per desktop (4x3 grid)
    const MAX_APPS_PER_DESKTOP = 12;

    // Sync desktops with pinned items when they change
    // Skip initial sync if we already have data from localStorage/API
    const initialSyncDoneRef = useRef(false);

    // Get modules that are in ANY group (to exclude from individual display)
    const getModulesInGroups = useCallback(() => {
        const modulesInGroups = new Set();
        groupsData.forEach(group => {
            if (group.modules) {
                group.modules.forEach(m => {
                    const navId = MODULE_NAV_MAP[m.code] || m.code?.toLowerCase();
                    if (navId) modulesInGroups.add(navId);
                });
            }
        });
        return modulesInGroups;
    }, [groupsData]);

    useEffect(() => {
        // Skip if pinnedItems is empty (not loaded yet)
        if (pinnedItems.length === 0) return;

        // Get modules that are in groups (to exclude from individual display)
        const modulesInGroups = getModulesInGroups();

        // Convert pinned items to identifiers, filtering out modules that are in groups
        const pinnedIds = pinnedItems
            .filter(item => {
                if (item.type === 'module') {
                    // Exclude modules that are in a group
                    return !modulesInGroups.has(item.id);
                }
                return true; // Keep groups
            })
            .map(item => `${item.type}:${item.id}`);
        const pinnedIdSet = new Set(pinnedIds);

        // Check if we already have layout data
        const hasExistingLayout = desktops.some(d => d.length > 0);

        if (hasExistingLayout && initialSyncDoneRef.current) {
            // Filter desktops to only include pinned items (removes unpinned items, keeping order)
            let newDesktops = desktops.map(desktop =>
                desktop.filter(id => pinnedIdSet.has(id))
            );

            // Find items that are pinned but not yet placed anywhere
            const allPlacedIds = new Set(newDesktops.flat());
            const unplacedItems = pinnedIds.filter(id => !allPlacedIds.has(id));

            // Add unplaced items to the appropriate desktop
            if (unplacedItems.length > 0) {
                if (newDesktops.length === 0) newDesktops = [[]];

                for (const itemId of unplacedItems) {
                    let placed = false;
                    for (let i = 0; i < newDesktops.length; i++) {
                        if (newDesktops[i].length < MAX_APPS_PER_DESKTOP) {
                            newDesktops[i].push(itemId);
                            placed = true;
                            break;
                        }
                    }
                    if (!placed) {
                        console.log('No space available for new pinned item:', itemId);
                    }
                }
            }

            // Remove empty desktops (except first one)
            newDesktops = newDesktops.filter((d, i) => i === 0 || d.length > 0);
            if (newDesktops.length === 0) newDesktops = [[]];

            // Update state if changed
            if (JSON.stringify(newDesktops) !== JSON.stringify(desktops)) {
                setDesktops(newDesktops);
                saveToDb(newDesktops);

                if (currentDesktop >= newDesktops.length) {
                    setCurrentDesktop(Math.max(0, newDesktops.length - 1));
                }
            }
        } else if (!hasExistingLayout) {
            // No existing layout - initialize from pinnedItems
            const initialDesktop = pinnedIds.slice(0, MAX_APPS_PER_DESKTOP);
            if (initialDesktop.length > 0) {
                setDesktops([initialDesktop]);
                saveToDb([initialDesktop]);
            }
        }

        initialSyncDoneRef.current = true;
    }, [pinnedItems]);

    // Prevent scrolling while dragging
    useEffect(() => {
        const preventScroll = (e) => {
            if (touchDragInfo) {
                e.preventDefault();
            }
        };
        document.addEventListener('touchmove', preventScroll, { passive: false });
        return () => document.removeEventListener('touchmove', preventScroll);
    }, [touchDragInfo]);

    // Save to DB (debounced)
    const saveToDb = (newDesktops) => {
        localStorage.setItem('cabazaDashboardDesktops', JSON.stringify(newDesktops));

        if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = setTimeout(async () => {
            if (!userId) return;
            try {
                await savePeoplePreferences(userId, {
                    dashboardDesktops: newDesktops
                });
            } catch (error) {
                console.error('Error saving dashboard preferences:', error);
            }
        }, 500);
    };

    // Get background from theme (same as sidebar)
    const hasImageBg = currentTheme?.sidebarBgImage;
    const themeGradient = (currentTheme?.primaryColor && currentTheme?.secondaryColor)
        ? `linear-gradient(135deg, ${currentTheme.primaryColor} 0%, ${currentTheme.secondaryColor} 100%)`
        : DEFAULT_GRADIENT;

    // Handle logout
    const handleLogout = () => {
        logout();
        navigate('/');
    };

    // Get group by id
    const getGroupById = (groupId) => {
        return groupsData.find(g => g.id === groupId || g.id === parseInt(groupId));
    };

    // Get module label
    const getModuleLabel = (navId) => {
        const labels = {
            contacts: t.menu?.contacts || 'Contacts',
            messages: t.menu?.messages || 'Messages',
            map: t.menu?.map || 'Carte',
            calendar: t.menu?.calendar || 'Agenda',
            events: t.menu?.events || 'Events',
            places: t.menu?.places || 'Endroits',
            support: t.menu?.support || 'Support',
            work: t.menu?.work || 'Demandes',
            offers: t.menu?.offers || 'Offres',
            products: t.menu?.products || 'Produits',
            advertisements: t.menu?.advertisements || 'Publicités',
            websites: t.menu?.websites || 'Sites web'
        };
        return labels[navId] || navId;
    };

    // Get dock app label
    const getDockAppLabel = (appId) => {
        const labels = {
            profile: t.menu?.profile || 'Profil',
            preferences: t.menu?.preferences || 'Préférences',
            search: t.menu?.search || 'Recherche',
            modules: t.menu?.modules || 'Modules'
        };
        return labels[appId] || appId;
    };

    // Parse item identifier (e.g., "module:contacts" or "group:123")
    const parseItemId = (itemId) => {
        if (!itemId || typeof itemId !== 'string') return null;
        const [type, id] = itemId.split(':');
        return { type, id: type === 'group' ? parseInt(id) : id };
    };

    // ========== DRAG & DROP (grid only) ==========

    const handleDragStart = (e, position) => {
        dragItem.current = { position };
        setDraggedId(gridApps[position]);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e, position) => {
        e.preventDefault();
        e.stopPropagation();
        const rect = e.currentTarget.getBoundingClientRect();
        const midX = rect.left + rect.width / 2;
        const side = e.clientX < midX ? 'left' : 'right';
        dragOverItem.current = { position, side };
        setDropTarget({ position, side });
    };

    const handleDragLeave = (e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) {
            setDropTarget(null);
        }
    };

    const handleContainerDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (!dropTarget) {
            dragOverItem.current = { position: gridApps.length, side: 'left' };
            setDropTarget({ position: gridApps.length, side: 'left' });
        }
        // Check edge zones for desktop drag
        if (draggedId) {
            checkEdgeDrag(e.clientX);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();

        // Check if this is a module being dropped from a group
        try {
            const dropData = e.dataTransfer.getData('text/plain');
            if (dropData) {
                const parsed = JSON.parse(dropData);
                if (parsed.type === 'module-from-group') {
                    handleModuleDropFromGroup(parsed);
                    resetDrag();
                    return;
                }
            }
        } catch (err) {
            // Not JSON data, continue with normal drop handling
        }

        if (!dragItem.current) {
            resetDrag();
            return;
        }

        const from = dragItem.current;
        const to = dragOverItem.current || { position: 0, side: 'left' };

        let insertPosition = to.position;
        if (to.side === 'right') insertPosition = to.position + 1;

        if (from.position === insertPosition || from.position === insertPosition - 1) {
            resetDrag();
            return;
        }

        let newDesktops = desktops.map(d => [...d]);
        let newGridApps = [...newDesktops[currentDesktop]];

        const item = newGridApps[from.position];
        newGridApps.splice(from.position, 1);
        const adjustedPos = from.position < insertPosition ? insertPosition - 1 : insertPosition;
        newGridApps.splice(adjustedPos, 0, item);

        newDesktops[currentDesktop] = newGridApps;
        setDesktops(newDesktops);
        saveToDb(newDesktops);
        resetDrag();
    };

    const resetDrag = () => {
        dragItem.current = null;
        dragOverItem.current = null;
        setDraggedId(null);
        setDropTarget(null);
        setGroupDropTarget(null);
        setEdgeDragZone(null);
        if (edgeDragTimer.current) {
            clearTimeout(edgeDragTimer.current);
            edgeDragTimer.current = null;
        }
    };

    const handleDragEnd = () => resetDrag();

    // Handle dropping a module from a group onto the dashboard
    const handleModuleDropFromGroup = async (dropData) => {
        const { moduleId, moduleCode, navId, groupId } = dropData;

        try {
            // 1. Remove module from group via API
            const result = await removeModuleFromGroup(groupId, moduleId);
            if (!result.success && !result.SUCCESS) {
                console.error('Failed to remove module from group:', result.message || result.MESSAGE);
                return;
            }

            // 2. Add module to pinnedItems as individual item (if not already there)
            const newPinnedItem = { type: 'module', id: navId };
            const alreadyPinned = pinnedItems.some(item => item.type === 'module' && item.id === navId);
            const updatedPinnedItems = alreadyPinned ? pinnedItems : [...pinnedItems, newPinnedItem];
            setPinnedItems(updatedPinnedItems);
            localStorage.setItem('cabazaPinnedItems', JSON.stringify(updatedPinnedItems));

            // 3. Add module to current desktop (if not already there)
            const moduleIdentifier = `module:${navId}`;
            let newDesktops = desktops.map(d => [...d]);

            // Check if module is already on any desktop
            const alreadyOnDesktop = newDesktops.some(d => d.includes(moduleIdentifier));

            if (!alreadyOnDesktop) {
                // Add to current desktop if there's space
                if (newDesktops[currentDesktop].length < MAX_APPS_PER_DESKTOP) {
                    newDesktops[currentDesktop].push(moduleIdentifier);
                } else {
                    // Find a desktop with space or create a new one
                    let placed = false;
                    for (let i = 0; i < newDesktops.length; i++) {
                        if (newDesktops[i].length < MAX_APPS_PER_DESKTOP) {
                            newDesktops[i].push(moduleIdentifier);
                            placed = true;
                            break;
                        }
                    }
                    if (!placed) {
                        newDesktops.push([moduleIdentifier]);
                    }
                }
            }

            setDesktops(newDesktops);
            saveToDb(newDesktops);

            // 4. Update groupsData to reflect the removal
            const updatedGroupsData = groupsData.map(g => {
                if (g.id === groupId || g.id === parseInt(groupId)) {
                    return {
                        ...g,
                        modules: (g.modules || []).filter(m =>
                            (m.moduleid || m.id) !== moduleId &&
                            (m.moduleid || m.id) !== parseInt(moduleId)
                        )
                    };
                }
                return g;
            });
            setGroupsData(updatedGroupsData);
            localStorage.setItem('cabazaGroupsData', JSON.stringify(updatedGroupsData));

            // 5. Close the group popup
            setOpenGroupPopup(null);

            // 6. Dispatch events to sync with other components
            window.dispatchEvent(new CustomEvent('pinnedItemsChanged'));
            window.dispatchEvent(new CustomEvent('modulesGroupsChanged'));

            // 7. Save pinnedItems to API
            if (userId) {
                await savePeoplePreferences(userId, {
                    pinnedItems: updatedPinnedItems
                });
            }
        } catch (error) {
            console.error('Error handling module drop from group:', error);
        }
    };

    // Handle dropping a module onto a group (to add it to the group)
    const handleDropModuleOnGroup = async (moduleNavId, modulePosition, targetGroupId) => {
        try {
            // Get the module code from navId
            const moduleCode = NAV_TO_CODE[moduleNavId];
            if (!moduleCode) {
                console.error('Unknown module navId:', moduleNavId);
                return;
            }

            // Find the module in allModules to get its ID
            const moduleData = allModules.find(m =>
                m.code === moduleCode || m.code?.toUpperCase() === moduleCode
            );
            if (!moduleData) {
                console.error('Module not found:', moduleCode);
                return;
            }

            const moduleId = moduleData.moduleid || moduleData.id;

            // 1. Add module to group via API
            const result = await addModuleToGroup(targetGroupId, moduleId);
            if (!result.success && !result.SUCCESS) {
                console.error('Failed to add module to group:', result.message || result.MESSAGE);
                return;
            }

            // 2. Remove module from pinnedItems (it's now in a group)
            const updatedPinnedItems = pinnedItems.filter(item =>
                !(item.type === 'module' && item.id === moduleNavId)
            );
            setPinnedItems(updatedPinnedItems);
            localStorage.setItem('cabazaPinnedItems', JSON.stringify(updatedPinnedItems));

            // 3. Remove module from current desktop
            const moduleIdentifier = `module:${moduleNavId}`;
            let newDesktops = desktops.map(d => d.filter(id => id !== moduleIdentifier));

            // Remove empty desktops (except first one)
            newDesktops = newDesktops.filter((d, i) => i === 0 || d.length > 0);
            if (newDesktops.length === 0) newDesktops = [[]];

            setDesktops(newDesktops);
            saveToDb(newDesktops);

            // 4. Update groupsData to reflect the addition
            const updatedGroupsData = groupsData.map(g => {
                if (g.id === targetGroupId || g.id === parseInt(targetGroupId)) {
                    return {
                        ...g,
                        modules: [...(g.modules || []), {
                            moduleid: moduleId,
                            code: moduleCode,
                            name: moduleData.name || moduleData[`name_${language}`],
                            ispinned: true
                        }]
                    };
                }
                return g;
            });
            setGroupsData(updatedGroupsData);
            localStorage.setItem('cabazaGroupsData', JSON.stringify(updatedGroupsData));

            // 5. Dispatch events to sync with other components
            window.dispatchEvent(new CustomEvent('pinnedItemsChanged'));
            window.dispatchEvent(new CustomEvent('modulesGroupsChanged'));

            // 6. Save pinnedItems to API
            if (userId) {
                await savePeoplePreferences(userId, {
                    pinnedItems: updatedPinnedItems
                });
            }

            // 7. Adjust current desktop if needed
            if (currentDesktop >= newDesktops.length) {
                setCurrentDesktop(Math.max(0, newDesktops.length - 1));
            }
        } catch (error) {
            console.error('Error handling module drop on group:', error);
        }
    };

    // Create a new folder
    const handleCreateFolder = async () => {
        if (!newFolderName.trim() || !userId) return;

        try {
            // Use the standard folder color (purple)
            const folderColor = '#8b5cf6';

            // Create the group via API
            const result = await createModuleGroup(userId, newFolderName.trim(), folderColor);
            if (!result.success && !result.SUCCESS) {
                console.error('Failed to create folder:', result.message || result.MESSAGE);
                return;
            }

            const newGroupId = result.groupId || result.GROUPID || result.id || result.ID;

            // Create the new group object
            const newGroup = {
                id: newGroupId,
                name: newFolderName.trim(),
                color: folderColor,
                modules: []
            };

            // Update groupsData
            const updatedGroupsData = [...groupsData, newGroup];
            setGroupsData(updatedGroupsData);
            localStorage.setItem('cabazaGroupsData', JSON.stringify(updatedGroupsData));

            // Add to pinnedItems
            const newPinnedItem = { type: 'group', id: newGroupId };
            const updatedPinnedItems = [...pinnedItems, newPinnedItem];
            setPinnedItems(updatedPinnedItems);
            localStorage.setItem('cabazaPinnedItems', JSON.stringify(updatedPinnedItems));

            // Add to current desktop
            const groupIdentifier = `group:${newGroupId}`;
            let newDesktops = desktops.map(d => [...d]);

            if (newDesktops[currentDesktop].length < MAX_APPS_PER_DESKTOP) {
                newDesktops[currentDesktop].push(groupIdentifier);
            } else {
                // Find space or create new desktop
                let placed = false;
                for (let i = 0; i < newDesktops.length; i++) {
                    if (newDesktops[i].length < MAX_APPS_PER_DESKTOP) {
                        newDesktops[i].push(groupIdentifier);
                        placed = true;
                        break;
                    }
                }
                if (!placed) {
                    newDesktops.push([groupIdentifier]);
                }
            }

            setDesktops(newDesktops);
            saveToDb(newDesktops);

            // Dispatch events
            window.dispatchEvent(new CustomEvent('pinnedItemsChanged'));
            window.dispatchEvent(new CustomEvent('modulesGroupsChanged'));

            // Save to API
            await savePeoplePreferences(userId, {
                pinnedItems: updatedPinnedItems
            });

            // Reset dialog
            setNewFolderName('');
            setShowCreateFolderDialog(false);
        } catch (error) {
            console.error('Error creating folder:', error);
        }
    };

    // Delete a folder (only if empty)
    const handleDeleteGroup = async (groupId) => {
        try {
            // Delete the group via API
            const result = await deleteModuleGroup(groupId);
            if (!result.success && !result.SUCCESS) {
                console.error('Failed to delete folder:', result.message || result.MESSAGE);
                return;
            }

            // Remove from groupsData
            const updatedGroupsData = groupsData.filter(g =>
                g.id !== groupId && g.id !== parseInt(groupId)
            );
            setGroupsData(updatedGroupsData);
            localStorage.setItem('cabazaGroupsData', JSON.stringify(updatedGroupsData));

            // Remove from pinnedItems
            const updatedPinnedItems = pinnedItems.filter(item =>
                !(item.type === 'group' && (item.id === groupId || item.id === parseInt(groupId)))
            );
            setPinnedItems(updatedPinnedItems);
            localStorage.setItem('cabazaPinnedItems', JSON.stringify(updatedPinnedItems));

            // Remove from desktops
            const groupIdentifier = `group:${groupId}`;
            let newDesktops = desktops.map(d => d.filter(id => id !== groupIdentifier));

            // Remove empty desktops (except first one)
            newDesktops = newDesktops.filter((d, i) => i === 0 || d.length > 0);
            if (newDesktops.length === 0) newDesktops = [[]];

            setDesktops(newDesktops);
            saveToDb(newDesktops);

            // Close the popup
            setOpenGroupPopup(null);

            // Dispatch events
            window.dispatchEvent(new CustomEvent('pinnedItemsChanged'));
            window.dispatchEvent(new CustomEvent('modulesGroupsChanged'));

            // Save to API
            if (userId) {
                await savePeoplePreferences(userId, {
                    pinnedItems: updatedPinnedItems
                });
            }

            // Adjust current desktop if needed
            if (currentDesktop >= newDesktops.length) {
                setCurrentDesktop(Math.max(0, newDesktops.length - 1));
            }
        } catch (error) {
            console.error('Error deleting folder:', error);
        }
    };

    // Move app to another desktop
    const moveAppToDesktop = (appId, fromPosition, targetDesktopIndex) => {
        let newDesktops = desktops.map(d => [...d]);

        newDesktops[currentDesktop] = newDesktops[currentDesktop].filter((_, i) => i !== fromPosition);

        while (newDesktops.length <= targetDesktopIndex) {
            newDesktops.push([]);
        }
        newDesktops[targetDesktopIndex].push(appId);
        newDesktops = newDesktops.filter((d, i) => i === 0 || d.length > 0);

        setDesktops(newDesktops);
        saveToDb(newDesktops);

        const newTargetIndex = Math.min(targetDesktopIndex, newDesktops.length - 1);
        setCurrentDesktop(newTargetIndex);
        appRefs.current = {};
        resetDrag();
    };

    // Check edge drag
    const checkEdgeDrag = (x) => {
        const screenWidth = window.innerWidth;
        const edgeZone = 50;

        if (x < edgeZone && currentDesktop > 0) {
            // Left edge - go to previous desktop
            if (edgeDragZone !== 'left') {
                setEdgeDragZone('left');
                if (edgeDragTimer.current) clearTimeout(edgeDragTimer.current);
                edgeDragTimer.current = setTimeout(() => {
                    if (draggedId && dragItem.current) {
                        moveAppToDesktop(draggedId, dragItem.current.position, currentDesktop - 1);
                    }
                }, 800);
            }
        } else if (x > screenWidth - edgeZone) {
            // Right edge - go to next desktop or create new one
            if (edgeDragZone !== 'right') {
                setEdgeDragZone('right');
                if (edgeDragTimer.current) clearTimeout(edgeDragTimer.current);
                edgeDragTimer.current = setTimeout(() => {
                    if (draggedId && dragItem.current) {
                        // Allow creating new desktop by moving to currentDesktop + 1
                        moveAppToDesktop(draggedId, dragItem.current.position, currentDesktop + 1);
                    }
                }, 800);
            }
        } else {
            if (edgeDragZone) {
                setEdgeDragZone(null);
                if (edgeDragTimer.current) {
                    clearTimeout(edgeDragTimer.current);
                    edgeDragTimer.current = null;
                }
            }
        }
    };

    // Navigate between desktops
    const goToDesktop = (index) => {
        if (index >= 0 && index < desktops.length) {
            setCurrentDesktop(index);
            appRefs.current = {};
        }
    };

    // Swipe handlers
    const swipeStartRef = useRef(null);
    const handleGridTouchStart = (e) => {
        if (touchDragInfo) return;
        swipeStartRef.current = { x: e.touches[0].clientX, time: Date.now() };
    };
    const handleGridTouchMove = (e) => {
        if (swipeStartRef.current && !touchDragInfo) {
            swipeStartRef.current.lastX = e.touches[0].clientX;
        }
    };
    const handleGridTouchEnd = (e) => {
        if (touchDragInfo || !swipeStartRef.current) return;
        const endX = e.changedTouches?.[0]?.clientX || swipeStartRef.current.lastX || swipeStartRef.current.x;
        const diff = endX - swipeStartRef.current.x;
        const timeDiff = Date.now() - swipeStartRef.current.time;

        if (timeDiff < 500 && Math.abs(diff) > 80) {
            if (diff > 0 && currentDesktop > 0) {
                goToDesktop(currentDesktop - 1);
            } else if (diff < 0 && currentDesktop < desktops.length - 1) {
                goToDesktop(currentDesktop + 1);
            }
        }
        swipeStartRef.current = null;
    };

    // ========== TOUCH DRAG & DROP ==========

    const handleTouchStart = (e, position) => {
        const touch = e.touches[0];
        touchStartPos.current = { x: touch.clientX, y: touch.clientY, position };

        longPressTimer.current = setTimeout(() => {
            const appId = gridApps[position];
            dragItem.current = { position };
            setDraggedId(appId);
            setTouchDragInfo({
                appId,
                startX: touch.clientX,
                startY: touch.clientY,
                currentX: touch.clientX,
                currentY: touch.clientY
            });
            if (navigator.vibrate) navigator.vibrate(50);
        }, 300);
    };

    const handleTouchMove = (e) => {
        const touch = e.touches[0];

        if (longPressTimer.current && touchStartPos.current) {
            const dx = Math.abs(touch.clientX - touchStartPos.current.x);
            const dy = Math.abs(touch.clientY - touchStartPos.current.y);
            if (dx > 10 || dy > 10) {
                clearTimeout(longPressTimer.current);
                longPressTimer.current = null;
            }
        }

        if (touchDragInfo) {
            setTouchDragInfo(prev => ({
                ...prev,
                currentX: touch.clientX,
                currentY: touch.clientY
            }));
            checkEdgeDrag(touch.clientX);
            const targetInfo = findDropTarget(touch.clientX, touch.clientY);
            if (targetInfo) {
                dragOverItem.current = targetInfo;
                setDropTarget(targetInfo);
            }
        }
    };

    const handleTouchEnd = () => {
        if (longPressTimer.current) {
            clearTimeout(longPressTimer.current);
            longPressTimer.current = null;
        }

        if (touchDragInfo && dragItem.current && dragOverItem.current) {
            performDrop();
        } else if (touchDragInfo) {
            resetDrag();
        }

        touchStartPos.current = null;
        setTouchDragInfo(null);
    };

    const findDropTarget = (x, y) => {
        for (const [key, ref] of Object.entries(appRefs.current)) {
            if (!ref) continue;
            const rect = ref.getBoundingClientRect();
            if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) {
                const [, indexStr] = key.split('-');
                const position = parseInt(indexStr, 10);
                const midX = rect.left + rect.width / 2;
                const side = x < midX ? 'left' : 'right';
                return { position, side };
            }
        }

        const gridEl = document.querySelector('[data-grid="true"]');
        if (gridEl) {
            const rect = gridEl.getBoundingClientRect();
            if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) {
                return { position: gridApps.length, side: 'left' };
            }
        }

        return null;
    };

    const performDrop = () => {
        if (!dragItem.current) {
            resetDrag();
            return;
        }

        const from = dragItem.current;
        const to = dragOverItem.current || { position: 0, side: 'left' };

        let insertPosition = to.position;
        if (to.side === 'right') insertPosition = to.position + 1;

        if (from.position === insertPosition || from.position === insertPosition - 1) {
            resetDrag();
            return;
        }

        let newDesktops = desktops.map(d => [...d]);
        let newGridApps = [...newDesktops[currentDesktop]];

        const item = newGridApps[from.position];
        newGridApps.splice(from.position, 1);
        const adjustedPos = from.position < insertPosition ? insertPosition - 1 : insertPosition;
        newGridApps.splice(adjustedPos, 0, item);

        newDesktops[currentDesktop] = newGridApps;
        setDesktops(newDesktops);
        saveToDb(newDesktops);
        resetDrag();
    };

    // Context menu state (for removing icons)
    const [contextMenu, setContextMenu] = useState(null); // { itemId, x, y, index }

    // Remove item from dashboard (not from pinnedItems, just from current layout)
    const removeItemFromDashboard = (index) => {
        let newDesktops = desktops.map(d => [...d]);
        newDesktops[currentDesktop] = newDesktops[currentDesktop].filter((_, i) => i !== index);

        // Remove empty desktops (except first one)
        newDesktops = newDesktops.filter((d, i) => i === 0 || d.length > 0);
        if (newDesktops.length === 0) newDesktops = [[]];

        setDesktops(newDesktops);
        saveToDb(newDesktops);
        setContextMenu(null);

        // Adjust current desktop if needed
        if (currentDesktop >= newDesktops.length) {
            setCurrentDesktop(Math.max(0, newDesktops.length - 1));
        }
    };

    // Handle right-click / long press to show context menu
    const handleContextMenu = (e, itemId, index) => {
        e.preventDefault();
        e.stopPropagation();
        setContextMenu({ itemId, x: e.clientX || e.touches?.[0]?.clientX, y: e.clientY || e.touches?.[0]?.clientY, index });
    };

    // Close context menu when clicking outside
    useEffect(() => {
        const handleClickOutside = () => setContextMenu(null);
        if (contextMenu) {
            document.addEventListener('click', handleClickOutside);
            document.addEventListener('touchstart', handleClickOutside);
            return () => {
                document.removeEventListener('click', handleClickOutside);
                document.removeEventListener('touchstart', handleClickOutside);
            };
        }
    }, [contextMenu]);

    // Background style (uses theme like sidebar)
    const bgStyle = hasImageBg
        ? { backgroundImage: `url(${currentTheme.sidebarBgImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }
        : { background: themeGradient };

    // Check if touch device
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

    // Render app icon (grid only - modules and groups)
    const renderAppIcon = (itemId, index) => {
        const parsed = parseItemId(itemId);
        if (!parsed) return null;

        const { type, id } = parsed;
        const isDragging = draggedId === itemId;
        const refKey = `grid-${index}`;

        const isDropLeft = dropTarget && dropTarget.position === index && dropTarget.side === 'left';
        const isDropRight = dropTarget && dropTarget.position === index && dropTarget.side === 'right';

        if (type === 'module') {
            const navId = id;
            const code = NAV_TO_CODE[navId];
            const Icon = MODULE_ICONS[code] || LayoutGrid;
            const color = MODULE_COLORS[code] || '#8b5cf6';
            const hasBadge = navId === 'messages' && unreadMessages > 0;

            return (
                <div
                    key={itemId}
                    ref={(el) => { appRefs.current[refKey] = el; }}
                    className="relative flex items-center"
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => { e.stopPropagation(); handleDrop(e); }}
                >
                    {isDropLeft && draggedId !== itemId && (
                        <div className="absolute -left-1 top-1/2 -translate-y-1/2 w-1 h-12 bg-white rounded-full z-10" />
                    )}

                    <div
                        draggable={!isTouchDevice}
                        onDragStart={!isTouchDevice ? (e) => handleDragStart(e, index) : undefined}
                        onDragEnd={!isTouchDevice ? handleDragEnd : undefined}
                        onTouchStart={(e) => handleTouchStart(e, index)}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}
                        onClick={() => !draggedId && !touchDragInfo && onNavigate(navId)}
                        onContextMenu={(e) => handleContextMenu(e, itemId, index)}
                        className={`flex flex-col items-center gap-1.5 p-2 rounded-2xl transition-all cursor-pointer select-none touch-none
                            ${isDragging ? 'opacity-40 scale-95' : 'hover:bg-white/10 active:scale-90'}`}
                    >
                        <div
                            className="w-14 h-14 rounded-2xl shadow-lg flex items-center justify-center relative"
                            style={{ backgroundColor: color }}
                        >
                            <Icon className="w-7 h-7 text-white" />
                            {hasBadge && (
                                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold shadow-md">
                                    {unreadMessages > 9 ? '9+' : unreadMessages}
                                </span>
                            )}
                        </div>
                        <span className="text-xs text-white font-medium text-center leading-tight max-w-[60px] truncate">
                            {getModuleLabel(navId)}
                        </span>
                    </div>

                    {isDropRight && draggedId !== itemId && (
                        <div className="absolute -right-1 top-1/2 -translate-y-1/2 w-1 h-12 bg-white rounded-full z-10" />
                    )}
                </div>
            );
        } else if (type === 'group') {
            const group = getGroupById(id);
            if (!group) return null;

            // Check if a module is being dragged (not a group)
            const isDraggingModule = draggedId && draggedId.startsWith('module:');
            const isGroupDropTarget = groupDropTarget === group.id;

            // Handle drag over group (for dropping modules into it)
            const handleGroupDragOver = (e) => {
                e.preventDefault();
                e.stopPropagation();
                if (isDraggingModule) {
                    setGroupDropTarget(group.id);
                    e.dataTransfer.dropEffect = 'move';
                } else {
                    handleDragOver(e, index);
                }
            };

            // Handle drag leave group
            const handleGroupDragLeave = (e) => {
                if (!e.currentTarget.contains(e.relatedTarget)) {
                    setGroupDropTarget(null);
                }
                handleDragLeave(e);
            };

            // Handle drop on group
            const handleGroupDrop = (e) => {
                e.preventDefault();
                e.stopPropagation();

                if (isDraggingModule && dragItem.current) {
                    // Get the module navId from the dragged item
                    const parsed = parseItemId(draggedId);
                    if (parsed && parsed.type === 'module') {
                        handleDropModuleOnGroup(parsed.id, dragItem.current.position, group.id);
                    }
                    resetDrag();
                } else {
                    handleDrop(e);
                }
            };

            return (
                <div
                    key={itemId}
                    ref={(el) => { appRefs.current[refKey] = el; }}
                    className="relative flex items-center"
                    onDragOver={handleGroupDragOver}
                    onDragLeave={handleGroupDragLeave}
                    onDrop={handleGroupDrop}
                >
                    {isDropLeft && draggedId !== itemId && !isDraggingModule && (
                        <div className="absolute -left-1 top-1/2 -translate-y-1/2 w-1 h-12 bg-white rounded-full z-10" />
                    )}

                    <div
                        draggable={!isTouchDevice}
                        onDragStart={!isTouchDevice ? (e) => handleDragStart(e, index) : undefined}
                        onDragEnd={!isTouchDevice ? handleDragEnd : undefined}
                        onTouchStart={(e) => handleTouchStart(e, index)}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}
                        onClick={() => !draggedId && !touchDragInfo && setOpenGroupPopup(group)}
                        onContextMenu={(e) => handleContextMenu(e, itemId, index)}
                        className={`flex flex-col items-center gap-1.5 p-2 rounded-2xl transition-all cursor-pointer select-none touch-none
                            ${isDragging ? 'opacity-40 scale-95' : 'hover:bg-white/10 active:scale-90'}
                            ${isGroupDropTarget ? 'ring-2 ring-white ring-offset-2 ring-offset-transparent scale-110' : ''}`}
                    >
                        <div
                            className={`w-14 h-14 rounded-2xl shadow-lg flex items-center justify-center relative transition-all
                                ${isGroupDropTarget ? 'scale-105' : ''}`}
                            style={{ backgroundColor: group.color || '#8b5cf6' }}
                        >
                            <Folder className="w-7 h-7 text-white" />
                            {group.modules?.length > 0 && (
                                <span className="absolute -bottom-1 -right-1 w-5 h-5 bg-white text-gray-700 text-xs rounded-full flex items-center justify-center font-bold shadow-md">
                                    {group.modules.length}
                                </span>
                            )}
                        </div>
                        <span className="text-xs text-white font-medium text-center leading-tight max-w-[60px] truncate">
                            {group.name}
                        </span>
                    </div>

                    {isDropRight && draggedId !== itemId && !isDraggingModule && (
                        <div className="absolute -right-1 top-1/2 -translate-y-1/2 w-1 h-12 bg-white rounded-full z-10" />
                    )}
                </div>
            );
        }

        return null;
    };

    // Get icon for floating drag
    const getDragIcon = (itemId) => {
        const parsed = parseItemId(itemId);
        if (!parsed) return null;

        if (parsed.type === 'module') {
            const code = NAV_TO_CODE[parsed.id];
            const Icon = MODULE_ICONS[code] || LayoutGrid;
            const color = MODULE_COLORS[code] || '#8b5cf6';
            return { Icon, color };
        } else if (parsed.type === 'group') {
            const group = getGroupById(parsed.id);
            return { Icon: Folder, color: group?.color || '#8b5cf6' };
        }
        return null;
    };

    // Handle drag over the entire dashboard (for edge detection)
    const handleDashboardDragOver = (e) => {
        e.preventDefault();
        if (draggedId) {
            checkEdgeDrag(e.clientX);
        }
    };

    // Handle mouse move for edge hover detection (show arrows when near edges)
    const handleMouseMove = useCallback((e) => {
        const screenWidth = window.innerWidth;
        const edgeZone = 60;
        const x = e.clientX;

        // Check left edge - only show if not on first desktop
        if (x < edgeZone && currentDesktop > 0) {
            setEdgeHover('left');
        }
        // Check right edge - only show if there's a next desktop
        else if (x > screenWidth - edgeZone && currentDesktop < desktops.length - 1) {
            setEdgeHover('right');
        }
        else {
            setEdgeHover(null);
        }
    }, [currentDesktop, desktops.length]);

    // Handle click on edge zones to navigate between desktops
    const handleEdgeClick = (direction) => {
        if (direction === 'left' && currentDesktop > 0) {
            goToDesktop(currentDesktop - 1);
        } else if (direction === 'right' && currentDesktop < desktops.length - 1) {
            goToDesktop(currentDesktop + 1);
        }
    };

    return (
        <>
        {/* MouseGlow cursor - rendered outside the main z-50 container to be above all modals */}
        <MouseGlow />

        <div
            className="fixed inset-0 z-50 overflow-hidden"
            style={bgStyle}
            onDragOver={handleDashboardDragOver}
            onMouseMove={handleMouseMove}
            onMouseLeave={() => setEdgeHover(null)}
        >

            {/* Overlay */}
            <div className="absolute inset-0 bg-black/20" />

            {/* Content */}
            <div className="relative z-10 flex flex-col h-full">
                {/* Header - Logo perfectly centered with language left and logout right */}
                <div className="flex items-center justify-between px-4 py-4">
                    {/* Left - Language Switcher */}
                    <div className="flex-1 flex justify-start">
                        <DashboardLanguageSwitcher />
                    </div>
                    {/* Center - Logo */}
                    <div className="bg-white rounded-xl px-2 py-1 shadow-lg">
                        <img src={logo} alt="Cabaza" className="h-7" />
                    </div>
                    {/* Right - Logout */}
                    <div className="flex-1 flex justify-end">
                        <button
                            onClick={handleLogout}
                            className="p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
                            title={t.menu?.logout || 'Logout'}
                        >
                            <LogOut className="w-5 h-5 text-white" />
                        </button>
                    </div>
                </div>

                {/* App Grid - 4x3 = 12 icons */}
                <div
                    className="flex-1 px-4 py-4 overflow-y-auto"
                    data-grid="true"
                    onDragOver={handleContainerDragOver}
                    onDrop={handleDrop}
                    onTouchStart={handleGridTouchStart}
                    onTouchMove={handleGridTouchMove}
                    onTouchEnd={handleGridTouchEnd}
                >
                    {gridApps.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full text-white/60">
                            <LayoutGrid className="w-16 h-16 mb-4 opacity-50" />
                            <p className="text-lg font-medium">{t.dashboard?.noPinnedItems || 'No pinned items'}</p>
                            <p className="text-sm mt-1">{t.dashboard?.pinFromModules || 'Pin modules from the Modules page'}</p>
                            <button
                                onClick={() => onNavigate('modules')}
                                className="mt-4 px-4 py-2 bg-white/20 rounded-full hover:bg-white/30 transition-colors"
                            >
                                {t.menu?.modules || 'Modules'}
                            </button>
                        </div>
                    ) : (
                        <div className="grid grid-cols-4 gap-4 w-fit mx-auto">
                            {gridApps.map((itemId, index) => renderAppIcon(itemId, index))}
                            {/* Create folder button */}
                            <div className="relative flex items-center">
                                <div
                                    onClick={() => setShowCreateFolderDialog(true)}
                                    className="flex flex-col items-center gap-1.5 p-2 rounded-2xl transition-all cursor-pointer select-none hover:bg-white/10 active:scale-90"
                                >
                                    <div className="w-14 h-14 rounded-2xl shadow-lg flex items-center justify-center bg-white/10 border-2 border-dashed border-white/30">
                                        <FolderPlus className="w-7 h-7 text-white/50" />
                                    </div>
                                    <span className="text-xs text-white/50 font-medium text-center leading-tight max-w-[60px] truncate">
                                        {t.dashboard?.newFolder || 'Nouveau'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Fixed Dock - Full responsive */}
                <div className="px-2 sm:px-4 pb-4">
                    <div className="bg-white/20 backdrop-blur-xl rounded-2xl sm:rounded-3xl p-2 sm:p-3 w-fit mx-auto">
                        <div className="flex justify-center items-center gap-1 sm:gap-3">
                            {DOCK_APPS.map((app) => {
                                const Icon = app.icon;
                                return (
                                    <button
                                        key={app.id}
                                        onClick={() => onNavigate(app.navId)}
                                        className="flex flex-col items-center p-1 sm:p-2 rounded-xl sm:rounded-2xl hover:bg-white/10 active:scale-90 transition-all"
                                    >
                                        <div
                                            className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl shadow-lg flex items-center justify-center"
                                            style={{ backgroundColor: app.color }}
                                        >
                                            <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* Desktop indicators */}
                <div className="flex justify-center gap-2 py-2">
                    {desktops.map((_, index) => (
                        <button
                            key={index}
                            onClick={() => goToDesktop(index)}
                            className={`w-2 h-2 rounded-full transition-all ${
                                index === currentDesktop
                                    ? 'bg-white w-6'
                                    : 'bg-white/40 hover:bg-white/60'
                            }`}
                        />
                    ))}
                    {draggedId && (
                        <div className="w-2 h-2 rounded-full bg-white/20 border border-white/40" />
                    )}
                </div>

                {/* Floating drag icon for touch */}
                {touchDragInfo && (() => {
                    const iconInfo = getDragIcon(touchDragInfo.appId);
                    if (!iconInfo) return null;
                    const { Icon, color } = iconInfo;
                    return (
                        <div
                            className="fixed pointer-events-none z-[9999]"
                            style={{
                                left: touchDragInfo.currentX - 28,
                                top: touchDragInfo.currentY - 28,
                            }}
                        >
                            <div
                                className="w-14 h-14 rounded-2xl shadow-2xl flex items-center justify-center opacity-90 scale-110"
                                style={{ backgroundColor: color }}
                            >
                                <Icon className="w-7 h-7 text-white" />
                            </div>
                        </div>
                    );
                })()}

                {/* Edge zone indicators - show on drag OR hover */}
                {/* Left edge - show if not on first desktop */}
                {currentDesktop > 0 && (
                    <div
                        className={`fixed left-0 top-0 bottom-0 w-16 z-[9998] flex items-center justify-center transition-all cursor-pointer ${
                            edgeDragZone === 'left' || edgeHover === 'left' ? 'bg-white/20' : 'bg-transparent'
                        }`}
                        onClick={() => handleEdgeClick('left')}
                    >
                        <ChevronLeft className={`w-10 h-10 text-white transition-all ${
                            edgeDragZone === 'left' ? 'opacity-100 animate-pulse scale-125' :
                            edgeHover === 'left' ? 'opacity-80 scale-110' : 'opacity-0'
                        }`} />
                    </div>
                )}

                {/* Right edge - show if there's another desktop */}
                {currentDesktop < desktops.length - 1 && (
                    <div
                        className={`fixed right-0 top-0 bottom-0 w-16 z-[9998] flex items-center justify-center transition-all cursor-pointer ${
                            edgeDragZone === 'right' || edgeHover === 'right' ? 'bg-white/20' : 'bg-transparent'
                        }`}
                        onClick={() => handleEdgeClick('right')}
                    >
                        <ChevronRight className={`w-10 h-10 text-white transition-all ${
                            edgeDragZone === 'right' ? 'opacity-100 animate-pulse scale-125' :
                            edgeHover === 'right' ? 'opacity-80 scale-110' : 'opacity-0'
                        }`} />
                    </div>
                )}

                {/* Home indicator */}
                <div className="flex justify-center pb-2">
                    <div className="w-32 h-1 bg-white/50 rounded-full" />
                </div>
            </div>

            {/* Context Menu for removing icons */}
            {contextMenu && (
                <div
                    className="fixed z-[9999] bg-white rounded-xl shadow-2xl py-2 min-w-[140px] animate-in fade-in zoom-in-95 duration-150"
                    style={{
                        left: Math.min(contextMenu.x, window.innerWidth - 160),
                        top: Math.min(contextMenu.y, window.innerHeight - 60)
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <button
                        onClick={() => removeItemFromDashboard(contextMenu.index)}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-red-600 hover:bg-red-50 transition-colors"
                    >
                        <Minus className="w-4 h-4" />
                        <span className="font-medium">{t.dashboard?.removeIcon || 'Retirer'}</span>
                    </button>
                </div>
            )}

            {/* Group Popup Modal */}
            {openGroupPopup && (
                <GroupPopupModal
                    group={openGroupPopup}
                    onClose={() => setOpenGroupPopup(null)}
                    onNavigate={onNavigate}
                    unreadMessages={unreadMessages}
                    t={t}
                    language={language}
                    onRemoveFromGroup={handleModuleDropFromGroup}
                    onDeleteGroup={handleDeleteGroup}
                />
            )}

            {/* Create Folder Dialog */}
            {showCreateFolderDialog && (
                <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[9999] p-4">
                    <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden">
                        {/* Header */}
                        <div
                            className="flex items-center justify-between p-4 text-white"
                            style={{ backgroundColor: '#8b5cf6' }}
                        >
                            <div className="flex items-center gap-3">
                                <FolderPlus className="w-6 h-6" />
                                <span className="font-semibold text-lg">
                                    {t.dashboard?.createFolder || 'Nouveau dossier'}
                                </span>
                            </div>
                            <button
                                onClick={() => {
                                    setNewFolderName('');
                                    setShowCreateFolderDialog(false);
                                }}
                                className="p-2 hover:bg-white/20 rounded-full transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Content */}
                        <div className="p-4">
                            <input
                                type="text"
                                value={newFolderName}
                                onChange={(e) => setNewFolderName(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleCreateFolder()}
                                placeholder={t.dashboard?.folderName || 'Nom du dossier'}
                                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 outline-none transition-all text-gray-800"
                                autoFocus
                            />
                        </div>

                        {/* Footer */}
                        <div className="flex gap-3 p-4 pt-0">
                            <button
                                onClick={() => {
                                    setNewFolderName('');
                                    setShowCreateFolderDialog(false);
                                }}
                                className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-medium hover:bg-gray-50 transition-colors"
                            >
                                {t.common?.cancel || 'Annuler'}
                            </button>
                            <button
                                onClick={handleCreateFolder}
                                disabled={!newFolderName.trim()}
                                className="flex-1 px-4 py-2.5 rounded-xl bg-purple-500 text-white font-medium hover:bg-purple-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {t.common?.create || 'Créer'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
        </>
    );
};

export default Dashboard;
